import React, { useMemo, useCallback } from 'react'
import Image from 'next/image'
import { IDiamond } from 'types/diamond.types'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
} from '@tanstack/react-table'
import s from './TableGridView.module.css'
import { diamondShapeImg } from '@utils/diamondShapes'
import grayIcon from '@publicAssets/images/ui/diamond-comparison/gray-compare-icon.png'
import goldIcon from '@publicAssets/images/ui/diamond-comparison/gold-compare-icon.png'
import Link from 'next/link'
import {
  useDiamondData,
  useDiamondFilterAPI,
  useDiamondFilterState,
} from 'context/diamondFilterContext'
import {
  DiamondDataContextType,
  DiamondFilterAPIContextType,
  DiamondFilterContextType,
} from 'types/diamondFilter.types'
import { formatUSDollar } from '@utils/format-price'
import { Pagination } from '@components/common'
import { hasValidVideoUrlFormat } from '../DiamondViewSlider/helper'

const shapes = {
  RB: 'Round',
  PR: 'Princess',
  CUS: 'Cushion',
  EC: 'Emerald',
  AS: 'Asscher',
  SQR: 'Square Radiant',
  OV: 'Oval',
  RAD: 'Radiant',
  MQ: 'Marquise',
  PS: 'Pear',
  HS: 'Heart',
  TR: 'Trillian',
}
const polsymm = {
  0: 'Poor',
  1: 'Fair',
  2: 'Good',
  3: 'Very Good',
  4: 'Excellent',
  5: 'Ideal',
}

interface Props {
  data: IDiamond[]
  loading: boolean
  itemsToCompare: Partial<IDiamond>[]
  setItemsToCompare: (arg: Partial<IDiamond>[]) => void
}

const TableGridView: React.FC<Props> = ({
  data,
  loading,
  itemsToCompare,
  setItemsToCompare,
}) => {
  const { diamondFilterState } =
    useDiamondFilterState() as DiamondFilterContextType
  const { onPageUpdate } = useDiamondFilterAPI() as DiamondFilterAPIContextType
  const { diamondMeta } = useDiamondData() as DiamondDataContextType
  const [columnVisibility, setColumnVisibility] = React.useState({})
  const [loadingDiamond, setLoadingDiamond] = React.useState(false)

  const onCompareClick = useCallback(
    (diamond: Partial<IDiamond>) => {
      if (!itemsToCompare.map((i) => i?.id).includes(diamond.id)) {
        setItemsToCompare([...itemsToCompare, diamond])
      } else {
        setItemsToCompare(
          itemsToCompare.filter((i: Partial<IDiamond>) => i.id !== diamond.id)
        )
      }
    },
    [itemsToCompare, setItemsToCompare]
  )

  const goToTop = () => {
    window.scrollTo({
      top: 700,
      behavior: 'smooth',
    })
  }

  const columns = useMemo<ColumnDef<IDiamond>[]>(
    () => [
      {
        header: '',
        id: 'diamondCard',
        cell: (info) => (
          <div
            /* className={cn({[s.loading]:loadingDiamond}, {[s.littleItem]:!loadingDiamond})} */
            className={s.littleItem}
          >
            <div className="text-center relative">
              <Link href={`/diamond/${info.row.original.id}`}>
                <a>
                  <div className={s.img}>
                    <Image
                      src={
                        diamondShapeImg[
                          info.row.original.shape?.toUpperCase() as keyof typeof diamondShapeImg
                        ]
                      }
                      alt={`${info.row.original.shape} diamond image`}
                      layout="fill"
                    />
                  </div>
                </a>
              </Link>
              <Link href={`/diamond/${info.row.original.id}`}>
                <a className={s.aFlex}>
                  <p className={s.propTitle}>
                    SHAPE:
                    <span className={s.span}>
                      {' '}
                      {shapes[info.row.original.shape as keyof typeof shapes]}
                    </span>
                  </p>
                  <p className={s.propTitle}>
                    WEIGHT:
                    <span className={s.span}> {info.row.original.weight}</span>
                  </p>
                  <p className={s.propTitle}>
                    COLOR:
                    <span className={s.span}> {info.row.original.color}</span>
                  </p>
                  <p className={s.propTitle}>
                    CLARITY:
                    <span className={s.span}> {info.row.original.clarity}</span>
                  </p>
                  <p className={s.propTitle}>
                    CUT:
                    <span className={s.span}> {info.row.original.cut}</span>
                  </p>
                  <p className={s.propTitle}>
                    SYMMETRY:
                    <span className={s.span}>
                      {' '}
                      {
                        polsymm[
                          info.row.original.symmetry as keyof typeof polsymm
                        ]
                      }
                    </span>
                  </p>
                  <p className={s.propTitle}>
                    POLISH:
                    <span className={s.span}>
                      {' '}
                      {
                        polsymm[
                          info.row.original.polish as keyof typeof polsymm
                        ]
                      }
                    </span>
                  </p>
                  <p className={s.propTitle}>
                    FLUORESCENCE:
                    <span className={s.span}> {info.row.original.fluor}</span>
                  </p>
                  <p className={s.propTitle}>
                    RATIO:
                    <span className={s.span}> {info.row.original.ratio}</span>
                  </p>
                  <p className={s.propTitle}>
                    STOCK ID:
                    <span className={s.span}>
                      {' '}
                      {`YD${info.row.original.id}`}
                    </span>
                  </p>
                  <p className={s.propTitle}>
                    LAB:
                    <span className={s.span}> {info.row.original.lab}</span>
                  </p>
                </a>
              </Link>
              {info.row.original.videourl &&
              hasValidVideoUrlFormat(info.row.original.videourl) ? (
                <a className={s.vidBtn} />
              ) : (
                ''
              )}

              <div className={s.footer}>
                <div className={s.comparison}>
                  <button
                    type="button"
                    onClick={() => onCompareClick(info.row.original)}
                  >
                    {itemsToCompare
                      .map((i) => i?.id)
                      .includes(info.row.original.id) ? (
                      <Image src={goldIcon} alt="" width={54} height={24} />
                    ) : (
                      <Image src={grayIcon} alt="" width={54} height={24} />
                    )}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-lg my-0 mx-auto">
                  {formatUSDollar(Math.round(Number(info.row.original.price)))}
                </label>
              </div>
              <div className="pb-[5px] px-[10px] h-[35px]">
                <a className={s.shapeInfo}>
                  {info.row.original.lab} {info.row.original.weight} carat{' '}
                  {info.row.original.shape} {info.row.original.color}{' '}
                  {info.row.original.clarity}
                </a>
                <Link href={`/diamond/${info.row.original.id}`}>
                  <a className={s.viewDetails}>VIEW DETAILS</a>
                </Link>
              </div>
            </div>
          </div>
        ),
      },
    ],
    [itemsToCompare, onCompareClick]
  )

  const table = useReactTable({
    data,
    columns,
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  React.useEffect(
    () => table.setPageSize(diamondFilterState.perPage),
    [diamondFilterState.perPage, table]
  )
  React.useEffect(() => {
    setLoadingDiamond(false)
  }, [data])
  return (
    <>
      <div className="hidden justify-around md:grid grid-cols-[220px_220px_220px] md:gap-4 lg:grid-cols-[213px_213px_213px_213px] xl:grid-cols-[250px_250px_250px_250px]">
        {table.getRowModel().rows.map((row) => {
          return (
            <div key={row.id}>
              {row.getVisibleCells().map((cell) => {
                return (
                  <div key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>
      <div className="flex items-center	align-middle justify-end mb-4 md:w-[96%] lg:w-[97%] xl:w-[97.5%] mx-auto">
        <div className="flex justify-end font-[inherit]">
          <Pagination
            top={700}
            setLoading={setLoadingDiamond}
            diamondFilterState={diamondFilterState}
            onPageUpdate={onPageUpdate}
            diamondMeta={diamondMeta.total_pages}
          />
        </div>
      </div>
    </>
  )
}

export default TableGridView
